import {checkRouteAccess} from '../utils/helpers/routes';
import {
  authenticatedRoutes,
  unauthenticatedRoutes,
  publicRoutes,
  PLATFORM_WEB,
  PLATFORM_NATIVE,
} from './manifest';

export {
  authenticatedRoutes,
  unauthenticatedRoutes,
  publicRoutes,
  checkRouteAccess,
  PLATFORM_WEB,
  PLATFORM_NATIVE,
};

/**
 * The screen key a route mounts. Defaults to the slug; `screen` overrides it
 * where one path can resolve to several screens (e.g. `home`).
 *
 * @param {object} route
 * @returns {string}
 */
export const screenFor = route => route.screen || route.slug;

/**
 * Filters a manifest to the entries valid on a platform.
 *
 * @param {string} target - PLATFORM_WEB or PLATFORM_NATIVE.
 * @returns {(route: object) => boolean}
 */
export const forPlatform = target => route =>
  !route.platforms || route.platforms.includes(target);

/**
 * Converts a web path pattern to Expo Router's file convention.
 *
 *   /properties/:id              -> /properties/[id]
 *   /profile/upgrade/:type?      -> /profile/upgrade/[type]
 *   /auth/verify/:id/:hash       -> /auth/verify/[id]/[hash]
 *
 * @param {string} path
 * @returns {string}
 */
export const toNativePath = path =>
  path.replace(/:([A-Za-z0-9_]+)\??/g, (_, name) => `[${name}]`);

/**
 * Substitutes params into a path pattern. Unfilled optional params are dropped
 * along with their trailing slash.
 *
 *   buildPath('/properties/:id', {id: 7})            -> '/properties/7'
 *   buildPath('/profile/upgrade/:type?', {})         -> '/profile/upgrade'
 *
 * @param {string} path
 * @param {object} params
 * @returns {string}
 */
export const buildPath = (path, params = {}) =>
  path
    .replace(/:([A-Za-z0-9_]+)(\?)?/g, (match, name, optional) => {
      const value = params[name];
      if (value !== undefined && value !== null) {
        return String(value);
      }
      return optional ? '' : match;
    })
    .replace(/\/+$/, '')
    .replace(/\/{2,}/g, '/') || '/';

/**
 * Every route the app knows about, in match order, for a given auth state.
 * Used for title lookup and deep-link resolution.
 *
 * @param {object} auth - Auth slice state, plus canAccessSelfCheck/hasProperties.
 * @param {string} target - Platform to filter to.
 * @returns {object[]}
 */
export const allRoutes = (auth = {}, target = PLATFORM_WEB) =>
  [
    ...authenticatedRoutes(auth),
    ...unauthenticatedRoutes(auth),
    ...publicRoutes(),
  ].filter(forPlatform(target));

/**
 * Pairs a manifest with a platform screen registry, dropping entries that have
 * no screen registered and no redirect to follow.
 *
 * This is the manifest/registry join. A slug absent from the registry simply
 * does not mount, which is how native opts out of web-only pages.
 *
 * @param {object[]} routes - Manifest entries.
 * @param {object} registry - Map of screen key -> component.
 * @param {string} target - Platform to filter to.
 * @returns {object[]} Entries with a resolved `Component`, plus redirects.
 */
export const resolveRoutes = (routes, registry, target = PLATFORM_WEB) =>
  routes
    .filter(forPlatform(target))
    .filter(checkRouteAccess)
    .map(route => ({...route, Component: registry[screenFor(route)] || null}))
    .filter(route => route.Component || route.redirectTo || route.aliasOf);

/**
 * Path pattern for a slug. Used to resolve `aliasOf` to its canonical path.
 *
 * @param {object[]} routes
 * @param {string} slug
 * @returns {string}
 */
export const pathForSlug = (routes, slug) =>
  routes.find(route => route.slug === slug)?.path || '/';

/**
 * Auth state in which every canonical screen is present in the manifest. Only
 * the entries' presence and paths matter here; their `canAccess` values are
 * never read.
 */
const FULL_ACCESS = {
  fetching: false,
  loggedIn: true,
  userVerified: true,
  canAccessSelfCheck: true,
  hasProperties: false,
};

/**
 * Every real screen the app has, independent of who is looking. Excludes the
 * `redirectTo` bounces and `aliasOf` shortcuts, which are not destinations.
 *
 * @returns {object[]}
 */
export const canonicalRoutes = () =>
  [
    ...authenticatedRoutes(FULL_ACCESS),
    ...unauthenticatedRoutes({loggedIn: false}),
    ...publicRoutes(),
  ].filter(route => !route.redirectTo && !route.aliasOf);

/**
 * Canonical path pattern an alias points at.
 *
 * Resolved against `canonicalRoutes()` rather than the manifest the alias sits
 * in: when logged out, that manifest only holds `unauth_*` bounce entries, so
 * looking up `viewings` there falls back to `/` and login's `back_url` loses
 * the destination. The target of an alias is the same regardless of auth state.
 *
 *   aliasTargetPath({aliasOf: 'viewing_details'}) -> '/viewings/:id'
 *
 * @param {object} route - Manifest entry with `aliasOf`.
 * @returns {string}
 */
export const aliasTargetPath = route =>
  pathForSlug(canonicalRoutes(), route.aliasOf);

/**
 * Whether an alias points at a public screen. Public screens need no login,
 * so an alias to one resolves straight to its target whatever the visitor's
 * auth state, instead of bouncing through login with a `back_url`.
 *
 *   aliasTargetIsPublic({aliasOf: 'request_data'}) -> true
 *   aliasTargetIsPublic({aliasOf: 'viewings'})     -> false
 *
 * @param {object} route - Manifest entry with `aliasOf`.
 * @returns {boolean}
 */
export const aliasTargetIsPublic = route =>
  publicRoutes().some(candidate => candidate.slug === route.aliasOf);

/**
 * Looks up a route title by slug. Falls back to the app name so callers can use
 * the result directly.
 *
 * @param {object[]} routes
 * @param {string} slug
 * @returns {string}
 */
export const titleForSlug = (routes, slug) =>
  routes.find(route => route.slug === slug)?.title || 'Tenant Portal';

/**
 * Nav entries for the authenticated side nav, in manifest order.
 *
 * @param {object[]} routes
 * @returns {object[]}
 */
export const sideNavRoutes = routes =>
  routes.filter(route => route.showInSideNav === true);

/**
 * Nav entries for the public header nav.
 *
 * @param {object[]} routes
 * @returns {object[]}
 */
export const navRoutes = routes => routes.filter(route => route.showInNav);

/**
 * Nav entries for the footer.
 *
 * @param {object[]} routes
 * @returns {object[]}
 */
export const footerRoutes = routes =>
  routes.filter(route => route.showInFooter);

/**
 * Whether a manifest entry renders a screen of its own. `redirectTo` bounces
 * and `aliasOf` shortcuts are URL rewrites, so they never sit in a trail.
 *
 * @param {object} route
 * @returns {boolean}
 */
const isScreen = route => !route.redirectTo && !route.aliasOf;

/**
 * The manifest entry a route sits under, for breadcrumbs and back-titles.
 *
 * An explicit `parent` slug wins. Otherwise the parent is inferred from the
 * path pattern: the longest proper prefix, cut at `/`, that another screen
 * route owns. Prefixes no route claims are skipped, so
 * `/contact/information-request` climbs to `/contact` and a page three
 * segments deep with no middle route climbs straight to its section.
 *
 *   parentRoute(routes, {path: '/applications/:id/view'}) -> application_overview
 *   parentRoute(routes, {path: '/applications'})          -> undefined
 *
 * @param {object[]} routes - Manifest entries, in match order.
 * @param {object} route
 * @returns {object|undefined}
 */
export const parentRoute = (routes, route) => {
  if (route.parent) {
    return routes.find(candidate => candidate.slug === route.parent);
  }
  const parents = route.path.split('/').filter(Boolean).slice(0, -1);
  // Longest prefix first, so the nearest owning route wins.
  const prefixes = parents.map(
    (_, index) => `/${parents.slice(0, parents.length - index).join('/')}`
  );
  return prefixes
    .map(prefix =>
      routes.find(candidate => isScreen(candidate) && candidate.path === prefix)
    )
    .find(Boolean);
};

/**
 * Ordered ancestor chain for a route, root first, ending with the route
 * itself. A `parent` cycle in the manifest stops at the first repeat.
 *
 *   routeTrail(routes, application_view)
 *     -> [applications, application_overview, application_view]
 *
 * @param {object[]} routes
 * @param {object} route
 * @returns {object[]}
 */
export const routeTrail = (routes, route) => {
  const climb = (current, seen) => {
    const parent = parentRoute(routes, current);
    if (!parent || seen.includes(parent.slug)) {
      return [];
    }
    return [...climb(parent, [...seen, parent.slug]), parent];
  };
  return route ? [...climb(route, [route.slug]), route] : [];
};

/**
 * Breadcrumb entries for a route, root first. Each entry's `href` has the
 * current URL params substituted in, so `/applications/:id` under
 * `/applications/:id/view` links to the application being viewed. The last
 * entry is the current page and is flagged `active`.
 *
 * @param {object[]} routes
 * @param {object} route - The matched manifest entry.
 * @param {object} params - Current URL params.
 * @returns {{slug: string, label: string, href: string, active: boolean}[]}
 */
export const breadcrumbsFor = (routes, route, params = {}) => {
  const trail = routeTrail(routes, route);
  return trail.map((entry, index) => ({
    slug: entry.slug,
    label: entry.label || '',
    href: buildPath(entry.path, params),
    active: index === trail.length - 1,
  }));
};
